/**
 * 법령 기반 요율 default fallback (2026 baseline)
 *
 * - Firestore (`config/rates/2026/current`) 도달 전·실패 시 안전 fallback.
 * - **Flutter `regulatory_rates_default.dart` 와 1:1 동기화 의무** — 한쪽만 변경 금지.
 * - 값 출처:
 *   · 4대보험: `src/utils/payrollCalculations.ts` 의 PENSION_RATE/HEALTH_RATE 등
 *   · 노동: `src/constants/laborLaw.ts` (가산률, MIN_WAGE, 209, 공휴일)
 *   · 부가세·카드공제: 설계문서 §3.7
 *
 * 신규 키 추가 시: ① 모델(`regulatoryRates.ts`) → ② 이 파일 → ③ Flutter 측 → ④ Firestore 시드
 */

import type { RegulatoryRates } from '../types/regulatoryRates';

export const ratesDefault2026: RegulatoryRates = {
  meta: {
    year: 2026,
    country: 'KR',
    schemaVersion: 1,
    effectiveFrom: '2026-01-01',
  },

  vat: {
    standardRate: 0.10,
    creditCard: {
      deductRate: 0.013,
      deductLimit: 10_000_000,
    },
    simplified: {
      threshold: 80_000_000,
      vatRate: 0.10,
    },
    effectiveFrom: '2026-01-01',
  },

  incomeTax: {
    // 2026 종합소득세 누진 구간 (참고용 — 근로소득은 간이세액표 사용)
    brackets: [
      { upTo: 14_000_000, rate: 0.06, deduction: 0 },
      { upTo: 50_000_000, rate: 0.15, deduction: 1_260_000 },
      { upTo: 88_000_000, rate: 0.24, deduction: 5_760_000 },
      { upTo: 150_000_000, rate: 0.35, deduction: 15_440_000 },
      { upTo: 300_000_000, rate: 0.38, deduction: 19_940_000 },
      { upTo: 500_000_000, rate: 0.40, deduction: 25_940_000 },
      { upTo: 1_000_000_000, rate: 0.42, deduction: 35_940_000 },
      { upTo: null, rate: 0.45, deduction: 65_940_000 },
    ],
    basicDeduction: 1_500_000,
    dependentDeduction: 1_500_000,
    effectiveFrom: '2026-01-01',
  },

  withholding: {
    businessIncome: 0.03,
    freelanceTax: 0.03,
    localTaxOfWithholding: 0.10,
    simplifiedExemptionThreshold: 1_060_000, // 근로소득 간이세액표 면제선
    dailyWorker: {
      rate: 0.06,
      creditRate: 0.55,
      dailyExempt: 150_000,
    },
    effectiveFrom: '2026-01-01',
  },

  socialInsurance: {
    nps: {
      totalRate: 0.095,
      employerShare: 0.0475,
      workerShare: 0.0475,
      maxBase: 6_370_000,
      minBase: 400_000,
      regionalRate: 0.095,
      effectiveFrom: '2026-01-01',
    },
    nhi: {
      totalRate: 0.0719,
      employerShare: 0.03595,
      workerShare: 0.03595,
      ltcRateOfNhi: 0.1281,
      effectiveFrom: '2026-01-01',
    },
    ei: {
      unemployment: {
        total: 0.018,
        employer: 0.009,
        worker: 0.009,
      },
      empSafety: {
        under150: 0.0025,
        priority150_1000: 0.0045,
        other150_1000: 0.0065,
        over1000: 0.0085,
      },
      effectiveFrom: '2026-01-01',
    },
    wci: {
      averageRate: 0.0147,
      commuteRate: 0.0006,
      effectiveFrom: '2026-01-01',
    },
    duruNuri: {
      employeeMax: 10,
      wageMax: 2_700_000,
      supportRate: 0.80,
    },
  },

  labor: {
    minWage: {
      hourly: 10_320,         // 2026 최저시급
      monthly209h: 2_156_880, // 10320 × 209
      daily8h: 82_560,        // 10320 × 8
      probationDiscount: 0.10,
    },
    workhour: {
      weeklyMax: 52,
      dailyLegal: 8,
      monthlyToHourlyDivisor: 209,
    },
    overtime: {
      weekday: 0.5,
      night: 0.5,
      holidayUnder8: 0.5,
      holidayOver8: 1.0,
      laborDay: 0.5,
    },
    weeklyRestMinHours: 15,
    annualLeave: {
      under1yMax: 11,
      over1yBase: 15,
    },
    severance: {
      minTenureDays: 365,
      formula: 'avgWage * 30 * years',
    },
    fiveEmployeeRule: true, // legacy
    companySize: {
      threshold: 5,
      under: {
        overtimeApplied: false,
        nightApplied: false,
        holidayApplied: false,
        annualLeaveApplied: false,
        weeklyHolidayApplied: true,
        severanceApplied: true,
        unfairDismissalProtected: false,
      },
      over: {
        overtimeApplied: true,
        nightApplied: true,
        holidayApplied: true,
        annualLeaveApplied: true,
        weeklyHolidayApplied: true,
        severanceApplied: true,
        unfairDismissalProtected: true,
      },
    },
    maternityLeaveDays: 90,
    parentalLeaveYears: 1,
    effectiveFrom: '2026-01-01',
  },

  nonTaxable: {
    mealAllowanceMonthlyMax: 200_000,
    childCareAllowanceMonthlyMax: 200_000,
    vehicleAllowanceMonthlyMax: 200_000,
    effectiveFrom: '2026-01-01',
  },

  policy: {
    publicHolidays: {
      // 매년 고정 공휴일 (laborLaw.ts FIXED_HOLIDAYS 와 동일)
      fixed: [
        [1, 1],   // 신정
        [3, 1],   // 삼일절
        [5, 5],   // 어린이날
        [6, 6],   // 현충일
        [8, 15],  // 광복절
        [10, 3],  // 개천절
        [10, 9],  // 한글날
        [12, 25], // 크리스마스
      ],
      // 변동 공휴일 (laborLaw.ts VARIABLE_HOLIDAYS 와 동일). 키는 연도 string.
      variable: {
        '2024': [[2, 9], [2, 10], [2, 11], [5, 15], [9, 16], [9, 17], [9, 18]],
        '2025': [[1, 28], [1, 29], [1, 30], [5, 5], [10, 5], [10, 6], [10, 7]],
        '2026': [[2, 16], [2, 17], [2, 18], [5, 24], [9, 24], [9, 25], [9, 26]],
      },
    },
    workersDayDate: [5, 1],
    effectiveFrom: '2026-01-01',
  },

  penalty: {
    unpaidWageInterestRate: 0.20,
    effectiveFrom: '2026-01-01',
  },
};
